import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, RotateCcw, Loader2, Package } from "lucide-react";
import { adminApi, apiError } from "@/lib/adminApi";
import { imgUrl, Spinner } from "@/components/admin/shared";

export function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingSku, setUploadingSku] = useState(null);
  const fileRefs = useRef({});

  const load = async () => {
    const p = await adminApi.get("/admin/products");
    setProducts(p.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (sku, file) => {
    if (!file) return;
    setUploadingSku(sku);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await adminApi.post(`/admin/products/${sku}/image`, fd);
      toast.success("Photo updated");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingSku(null);
    }
  };

  const onReset = async (sku) => {
    try {
      await adminApi.delete(`/admin/products/${sku}/image`);
      toast.success("Reverted to default photo");
      await load();
    } catch {
      toast.error("Reset failed");
    }
  };

  if (loading) return <Spinner />;

  return (
    <div data-testid="products-panel">
      <h1 className="font-display text-3xl font-light text-[#2C2B2C]">Product Photos</h1>
      <p className="mt-2 text-sm text-[#8A7F86]">The catalog image shown on shop cards, cart and order emails.</p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-products-grid">
        {products.map((p) => (
          <div key={p.sku} className="overflow-hidden rounded-3xl border border-[#E5DBC8] bg-white" data-testid={`admin-product-${p.sku}`}>
            <div className="aspect-square overflow-hidden bg-white"><img src={imgUrl(p.image)} alt={p.name} className="h-full w-full object-cover" /></div>
            <div className="p-5">
              <div className="flex items-center gap-2"><Package className="h-4 w-4 text-[#FBC4A8]" /><p className="text-sm font-medium text-[#2C2B2C]">{p.name}</p></div>
              <p className="mt-1 text-xs text-[#8A7F86]">{p.sku}{p.overridden && " · custom photo"}</p>
              <input ref={(el) => (fileRefs.current[p.sku] = el)} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden" data-testid={`upload-input-${p.sku}`} onChange={(e) => onUpload(p.sku, e.target.files?.[0])} />
              <div className="mt-4 flex gap-2">
                <button onClick={() => fileRefs.current[p.sku]?.click()} disabled={uploadingSku === p.sku} data-testid={`upload-btn-${p.sku}`}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[#232222] py-2.5 text-[0.62rem] uppercase tracking-wider text-[#F5EFE3] hover:bg-[#4A2A38] disabled:opacity-60">
                  {uploadingSku === p.sku ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Replace
                </button>
                {p.overridden && (
                  <button onClick={() => onReset(p.sku)} data-testid={`reset-btn-${p.sku}`}
                    className="flex items-center justify-center rounded-full border border-[#DACDB4] px-3 text-[#8A7F86] hover:border-[#FBC4A8]"><RotateCcw className="h-3.5 w-3.5" /></button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
